import * as Application from "expo-application";
import Constants from "expo-constants";
import React, { memo } from "react";

import { version } from "../../../../../../package.json";
import Header from "@/features/auth/components/organisms/Header";

import { LogoIcon } from "@/components/molecules/icons";
import { Box } from "@/components/atoms/Box";
import { KeyboardAvoidingView } from "@/components/atoms/KeyboardAvoidingView";
import { Icon } from "@/components/atoms/Icon";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import { RAW_EXPO_PUBLIC_ENV } from "@/lib/env";
import { ifWeb } from "@/utils/platform";

/**
 *
 */
export interface IForm {
  mobileNumber: string;
}

/**
 * Builds the keyboard-avoiding root style: `flex: 1` plus the device top
 * safe-area inset as paddingTop (SE-5549), so the logo clears the status bar /
 * notch. Hoisted to a module const so it reaches the (non-atom)
 * KeyboardAvoidingView via a single-brace style prop rather than an inline
 * object literal. On web the inset is 0, leaving web top spacing className-driven.
 * @param topInset - Device top safe-area inset in pixels
 * @returns The root style object
 */
const buildRootStyle = (topInset: number) => ({
  flex: 1,
  paddingTop: topInset,
});

/**
 * ScrollView content-container fill so the hero + spacer + card lay out like a
 * flex column when short, and become scrollable when the keyboard shrinks the
 * viewport — keeping the focused input (the OTP cells) reachable above the
 * keyboard (SE-5549).
 */
const SCROLL_CONTENT_STYLE = { flexGrow: 1 } as const;

/**
 * Builds the content container's width style. The width is a runtime prop
 * (measured window width) with no closed token; this module-level helper
 * lets the View pass it via a single-brace `style` prop (rather than an
 * inline object literal) while keeping the arrow-shorthand View shape.
 * @param widthContent - Container width in pixels
 * @returns The style object setting the container width
 */
const buildContentStyle = (widthContent: number) => ({
  width: widthContent,
});

/**
 *
 */
interface Props {
  title: string;
  widthContent: number;
  subTitle: string;
  children: React.ReactNode;
  footer: React.ReactNode;
  backToSignIn?: boolean;
  isDisableButtonBack?: boolean;
  isDark: boolean;
  topInset?: number;
}

/**
 * View component for authentication screen layout with logo, branding, and form content
 * @param root0 - Component props
 * @param root0.subTitle - Descriptive text displayed below the screen title
 * @param root0.title - Main heading text for the authentication screen
 * @param root0.children - Form inputs and content to render in the main content area
 * @param root0.footer - Action buttons and links to render at the bottom of the form
 * @param root0.widthContent - Width value for the content container in pixels
 * @param root0.backToSignIn - Boolean indicating whether to show the back to sign in navigation
 * @param root0.isDisableButtonBack - Boolean indicating whether the back button should be disabled during loading
 * @param root0.isDark - Boolean indicating dark theme is active for conditional styling
 * @param root0.topInset - Device top safe-area inset in pixels
 */
const ScreenView = ({
  subTitle,
  title,
  children,
  footer,
  widthContent,
  backToSignIn = false,
  isDisableButtonBack = false,
  isDark,
  topInset = 0,
}: Props) => (
  <KeyboardAvoidingView
    // eslint-disable-next-line no-restricted-syntax -- runtime device top safe-area inset (paddingTop) on a bare re-export atom with no UNSAFE_style prop; static flex handled by buildRootStyle
    style={buildRootStyle(topInset)}
    behavior="padding"
    keyboardVerticalOffset={0}
  >
    <ScrollView
      className="flex-1 bg-surface-base"
      contentContainerStyle={SCROLL_CONTENT_STYLE}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      {/* SE-5549: brand lockup — the logo sits at the LEFT (self-start) with
          the wordmark centered below it. The hero has a natural height (not a
          flex-1/justify-between stretch) and the flex-1 spacer below pushes the
          form card down without letting a tall card (the OTP screen) overlap
          and clip the wordmark. The whole screen scrolls (ScrollView) so the
          keyboard can't cover the OTP cells. The hero is sized down on phones
          and scaled up on wider screens so it never overflows narrow viewports
          (fixed text-10xl = 160px clipped at ≤430px); the proportional
          -translate overlap (~20% of the font size at each step) preserves the
          lockup. SE-6800b named the "Sports" brand-gold as `brand-wordmark`
          (a closed annex outside the semantic-token budget) — the value is
          unchanged, so this renders exactly as before. The card surface is
          still untokenised, tracked in SE-5553. */}
      <Stack className={`px-6 ${ifWeb("pt-12", "pt-6")} gap-6`}>
        <Icon
          testID="SIGNIN.LOGO"
          as={LogoIcon}
          size="xl"
          className="scale-125 self-start"
        />
        <Stack testID="SIGNIN.TITLE_BLOCK" className="items-center">
          <Text
            variant="unstyled"
            className="font-bebas text-display-2xl leading-none text-content-primary sm:text-8xl md:text-10xl"
          >
            Gemini
          </Text>
          <Text
            variant="unstyled"
            testID="SIGNIN.TITLE_SPORTS"
            className="web:scale-x-90 origin-center font-bebas text-display-2xl leading-none text-brand-wordmark -translate-y-3 sm:text-8xl sm:-translate-y-5 md:text-10xl md:-translate-y-8"
          >
            Sports
          </Text>
        </Stack>
      </Stack>
      <Stack className="flex-1" />
      <Stack
        className={`self-center rounded-t-md ${isDark ? "bg-background-gray" : "bg-surface-raised"} py-8`}
        // eslint-disable-next-line no-restricted-syntax -- runtime measured window width; no closed sizing token
        UNSAFE_style={buildContentStyle(widthContent)}
      >
        <Box className="gap-10 px-3">
          <Stack className="gap-10">
            <Header
              title={title}
              subTitle={subTitle}
              backToSignIn={backToSignIn}
              isDisableButtonBack={isDisableButtonBack}
            />
            {children}
          </Stack>
          <Stack className="web:pb-10 px-3 pb-16">{footer}</Stack>
        </Box>
        <Stack className="gap-2 p-4 pt-0">
          {/* SE-7834: the RAW value — this label reports what the build said,
              so an unconfigured build reads blank rather than "production". */}
          <Text variant="caption">
            {`v${version} ${(RAW_EXPO_PUBLIC_ENV ?? "").replace(/production/g, "")} ${
              // @ts-expect-error runtimeVersion is missing from types even though it's valid
              Constants?.easConfig?.runtimeVersion ??
              Constants?.manifest2?.runtimeVersion
            } ${Application.nativeBuildVersion ? `Build: ${Application.nativeBuildVersion}` : ""}`}
          </Text>
        </Stack>
      </Stack>
    </ScrollView>
  </KeyboardAvoidingView>
);

ScreenView.displayName = "ScreenView";

export default memo(ScreenView);
